import React, { memo, useCallback, useEffect, useMemo, useRef, } from 'react';
import { View, ScrollView } from 'react-native';
import { runOnJS, useAnimatedReaction, } from 'react-native-reanimated';
import { rem } from 'design-system/values';
import { NavigationHeader } from 'design-system/NavigationHeader';
import SelectorControllableReanimated, { DEFAULT_SPACE_BETWEEN, } from 'design-system/SelectorControllableReanimated';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import NotificationsListScreenHeaderOverlay from 'features/feed/components/walkthrough/notification/NotificationsListScreenHeaderOverlay';
import { isWeb } from '../../../constants';
import NotificationCount from './NotificationCount';
const getSpaceName = (name, spacesLength) => {
    const truncateLength = spacesLength > 2 ? 15 : 20;
    if (name === 'DEFAULT') {
        return ['Private Space', rem('Private Space'.length * 8 + 32)];
    }
    const nameWithSpace = `${name}`;
    if (nameWithSpace.length > truncateLength) {
        return [
            `${nameWithSpace.slice(0, truncateLength).trim()}...`,
            rem((truncateLength + 1) * 8 + 38),
        ];
    }
    return [nameWithSpace, rem(nameWithSpace.length * 8 + 32)];
};
const NotificationsListScreenHeader = ({ spaces, scrollX, scrollY, horizontalScrollViewRef, displayCounts, setDisplayCounts, resetUnreadCount, prevSpaceIndex, setPrevSpaceIndex, isWalkthroughActive = false, }) => {
    const styles = useStyles(styleSet);
    const headerScrollViewRef = useRef(null);
    const spaceBetween = DEFAULT_SPACE_BETWEEN;
    const { contentWidth: width, paddingHorizontal } = useAppFrameDimensions(isWeb);
    useEffect(() => {
        if (!spaces || spaces.length === 0) {
            return;
        }
        resetUnreadCount(spaces[0].id);
    }, [resetUnreadCount, spaces]);
    const sections = useMemo(() => spaces.map((item) => {
        const [text, width] = getSpaceName(item.name, spaces.length);
        return {
            ...item,
            id: item.id,
            text,
            width: rem(width),
            notificationCount: (<NotificationCount count={displayCounts?.[item.id] || 0} top={rem(-4)} right={rem(-2)}/>),
        };
    }), [spaces, displayCounts]);
    const contentContainerStyle = useMemo(() => ({
        paddingHorizontal: paddingHorizontal + rem(16),
    }), [paddingHorizontal]);
    const centerOnItem = useCallback((index) => {
        if (headerScrollViewRef.current && sections[index]) {
            const item = sections[index];
            const itemsX = sections
                .slice(0, index)
                .reduce((acc, sec) => acc + sec.width + spaceBetween, 0);
            const itemWidth = item.width;
            const scrollViewWidth = width - contentContainerStyle.paddingHorizontal * 2;
            const scrollToX = itemsX - scrollViewWidth / 2 + itemWidth / 2;
            headerScrollViewRef.current.scrollTo({
                x: scrollToX,
                animated: true,
            });
        }
    }, [sections, width, contentContainerStyle.paddingHorizontal, spaceBetween]);
    const handleSelectorPress = useCallback((index) => {
        horizontalScrollViewRef.current?.scrollTo({ x: index * width, y: 0 });
        const space = spaces[index];
        if (space) {
            runOnJS(resetUnreadCount)(space.id);
            const prevSpace = spaces[prevSpaceIndex];
            if (prevSpace) {
                setDisplayCounts((prev) => ({
                    ...prev,
                    [prevSpace.id]: 0,
                }));
            }
            setPrevSpaceIndex(index);
        }
    }, [
        horizontalScrollViewRef,
        width,
        spaces,
        resetUnreadCount,
        prevSpaceIndex,
        setPrevSpaceIndex,
        setDisplayCounts,
    ]);
    useAnimatedReaction(() => Math.round(scrollX.value / width), (currentPage, previousPage) => {
        if (currentPage !== previousPage) {
            runOnJS(centerOnItem)(currentPage);
        }
    }, [centerOnItem, scrollX, width, spaces, resetUnreadCount]);
    return (<NavigationHeader scrollY={scrollY} withFrame={false} overlay={NotificationsListScreenHeaderOverlay}>
      <View style={styles.selectorContainer} pointerEvents={isWalkthroughActive ? 'none' : 'auto'}>
        {spaces.length > 1 ? (<ScrollView horizontal ref={headerScrollViewRef} showsHorizontalScrollIndicator={false} contentContainerStyle={contentContainerStyle} scrollEnabled={!isWalkthroughActive}>
            <SelectorControllableReanimated scrollX={scrollX} sections={sections} screenWidth={width} selectorStyle={styles.selectorStyle} itemContainerStyle={styles.itemContainerStyle} setSelection={handleSelectorPress} style={styles.marginTop}/>
          </ScrollView>) : null}
      </View>
    </NavigationHeader>);
};
export default memo(NotificationsListScreenHeader);
const styleSet = createStyleSheets(() => ({
    row: {
        flexDirection: 'row',
        padding: rem(16),
    },
    selectorContainer: {
        paddingBottom: rem(12),
    },
    itemContainerStyle: {
        padding: 0,
        margin: 0,
    },
    selectorStyle: {
        borderRadius: rem(8),
        justifyContent: 'center',
        padding: 0,
        margin: 0,
    },
    marginTop: { marginTop: rem(8) },
    paddingHorizontal: {
        paddingHorizontal: rem(16),
    },
}));
